import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ChevronRight, FileText, FolderOpen, Pencil } from 'lucide-react';
import { useAuth } from '../lib/security';
import WikiDocsNav from './WikiDocsNav';
import {
    useWikiDocs,
    WikiTOC,
    WikiArticleSummary,
    findSection,
    findSectionPath,
    articlesIn,
    articlesUnder,
} from '../utils/wikiToc';

/**
 * A section hub: the tree for the whole top-level area on the left, and an
 * overview of this section's articles and sub-sections in the middle. Choosing
 * an article hands over to the article page, which keeps the same left rail.
 */
const WikiSectionPage: React.FC = () => {
    const { tocId } = useParams<{ tocId: string }>();
    const { canWrite, canAdmin } = useAuth();
    const { sections, articles, loading, error } = useWikiDocs();

    if (loading) {
        return (
            <div className="bx-wrap py-16 flex justify-center">
                <div className="spinner"></div>
            </div>
        );
    }

    const path = findSectionPath(sections, tocId);
    const section = findSection(sections, tocId);

    if (error) {
        return (
            <div className="bx-wrap py-8">
                <div className="mx-auto max-w-3xl p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                </div>
            </div>
        );
    }

    // A deleted or mistyped section falls back to the landing page.
    if (!section) return <Navigate to="/wiki" replace />;

    const rootSection = path[0];
    const directArticles = articlesIn(articles, section.id);
    const childSections = section.children || [];
    const totalArticles = articlesUnder(articles, section).length;

    const renderArticleRow = (article: WikiArticleSummary) => (
        <Link
            key={article.id}
            to={`/wiki/article/${article.id}`}
            className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50 transition-colors group"
        >
            <span className="flex items-start gap-2.5 min-w-0">
                <FileText className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                <span className="min-w-0">
                    <span className="block text-sm text-gray-800 group-hover:text-brand-700 break-words">
                        {article.title}
                    </span>
                    <span className="block text-xs text-gray-400 mt-0.5">
                        Updated {new Date(article.updatedAt).toLocaleDateString()}
                        {article.updatedByName && ` by ${article.updatedByName}`}
                    </span>
                </span>
            </span>
            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-600 shrink-0" />
        </Link>
    );

    const renderChildSection = (child: WikiTOC) => {
        const childArticles = articlesIn(articles, child.id);
        const nested = child.children || [];
        const childTotal = articlesUnder(articles, child).length;

        return (
            <div key={child.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                    <Link
                        to={`/wiki/section/${child.id}`}
                        className="flex items-center gap-2 min-w-0 group"
                    >
                        <FolderOpen className="w-4 h-4 text-brand-600 shrink-0" />
                        <h2 className="text-base font-semibold text-gray-900 group-hover:text-brand-700 break-words">
                            {child.description}
                        </h2>
                    </Link>
                    <span className="shrink-0 text-xs text-gray-400">
                        {childTotal} {childTotal === 1 ? 'article' : 'articles'}
                    </span>
                </div>

                {child.summary?.trim() && (
                    <p className="px-5 pt-3 text-sm text-gray-500">{child.summary.trim()}</p>
                )}

                {childArticles.length > 0 ? (
                    <div className="divide-y divide-gray-100">{childArticles.map(renderArticleRow)}</div>
                ) : (
                    <p className="px-5 py-3 text-sm text-gray-400 italic">No articles directly in this sub-section.</p>
                )}

                {nested.length > 0 && (
                    <div className="px-5 py-3 border-t border-gray-100 flex flex-wrap gap-2">
                        {nested.map(grandchild => (
                            <Link
                                key={grandchild.id}
                                to={`/wiki/section/${grandchild.id}`}
                                className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:border-brand-300 hover:text-brand-700 transition-colors"
                            >
                                {grandchild.description}
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="bx-wrap py-8 fade-in">
            <div className="flex gap-8">
                <WikiDocsNav
                    section={rootSection}
                    articles={articles}
                    activeId={section.id}
                    activeSectionId={section.id}
                />

                <div className="flex-1 min-w-0">
                    {/* Breadcrumb */}
                    <nav className="flex items-center flex-wrap gap-1 text-sm text-gray-500 mb-4">
                        <Link to="/wiki" className="hover:text-brand-600">Documentation</Link>
                        {path.map((crumb, i) => (
                            <React.Fragment key={crumb.id}>
                                <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                                {i === path.length - 1 ? (
                                    <span className="text-gray-700">{crumb.description}</span>
                                ) : (
                                    <Link to={`/wiki/section/${crumb.id}`} className="hover:text-brand-600">
                                        {crumb.description}
                                    </Link>
                                )}
                            </React.Fragment>
                        ))}
                    </nav>

                    <div className="flex items-start justify-between gap-4 mb-6">
                        <div className="max-w-3xl">
                            <h1 className="text-3xl font-bold text-gray-800 break-words">{section.description}</h1>
                            {section.summary?.trim() && (
                                <p className="text-gray-500 mt-2 leading-relaxed">{section.summary.trim()}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                                {totalArticles} {totalArticles === 1 ? 'article' : 'articles'} in this section
                            </p>
                        </div>
                        {canAdmin('wiki') && (
                            <Link
                                to="/wiki/contents"
                                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                            >
                                <Pencil className="w-4 h-4" />
                                Edit contents
                            </Link>
                        )}
                    </div>

                    {/* This section's own articles */}
                    {directArticles.length > 0 && (
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
                            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                                    In this section
                                </h2>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {directArticles.map(renderArticleRow)}
                            </div>
                        </div>
                    )}

                    {/* Sub-sections */}
                    {childSections.length > 0 && (
                        <div className="space-y-6">{childSections.map(renderChildSection)}</div>
                    )}

                    {directArticles.length === 0 && childSections.length === 0 && (
                        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
                            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">No articles in this section yet.</p>
                            {canWrite('wiki') && (
                                <Link to="/wiki/edit" className="text-sm text-brand-600 hover:underline mt-2 inline-block">
                                    Write the first one
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WikiSectionPage;
